import { useState, useEffect, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Calendar, FileText, FlaskConical, Pill, LogOut, 
  Menu, X, Home, ChevronRight, Phone, Mail, Heart,
  Building2, ArrowRight, Shield, RefreshCw, Activity,
  Receipt, Clock, CheckCircle, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function for API calls using XMLHttpRequest to avoid fetch body stream issues
const apiRequest = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    // Add authorization header if provided
    if (options.headers?.Authorization) {
      xhr.setRequestHeader('Authorization', options.headers.Authorization);
    }
    
    xhr.onload = () => {
      try {
        const response = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(response);
        } else {
          reject(new Error(response.detail || 'Request failed'));
        }
      } catch (e) {
        reject(new Error('Server returned an invalid response'));
      }
    };
    
    xhr.onerror = () => reject(new Error('Network error. Please try again.'));
    
    if (options.body) {
      xhr.send(options.body);
    } else {
      xhr.send();
    }
  });
};

// Context for multi-tenant patient portal
const PortalContext = createContext(null);
export const usePortal = () => useContext(PortalContext);

// ==================== LOGIN COMPONENT ====================
function MultiTenantLogin({ onLogin }) {
  const [step, setStep] = useState('identifier'); // identifier, otp
  const [identifier, setIdentifier] = useState('');
  const [identifierType, setIdentifierType] = useState('phone');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const formatIdentifier = (value) => {
    if (identifierType === 'phone') {
      return value.replace(/\D/g, '').slice(0, 10);
    }
    return value;
  };

  const requestOTP = async () => {
    if (!identifier.trim()) {
      toast.error(`Please enter your ${identifierType === 'phone' ? 'phone number' : 'email'}`);
      return;
    }
    
    setLoading(true);
    try {
      // Use XMLHttpRequest to avoid fetch body stream issues
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/patient-portal-v2/request-otp`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = () => {
          try {
            const response = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(response);
            } else {
              reject(new Error(response.detail || 'Failed to send OTP'));
            }
          } catch (e) {
            reject(new Error('Server returned an invalid response'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error. Please try again.'));
        xhr.send(JSON.stringify({ 
          identifier: identifier.trim(), 
          identifier_type: identifierType 
        }));
      });
      
      toast.success(data.message);
      if (data.hospitals_count) {
        toast.info(`Found records in ${data.hospitals_count} hospital(s)`);
      }
      setStep('otp');
      setCountdown(60);
      
      // Show dev OTP if available
      if (data.dev_otp) {
        toast.info(`Dev OTP: ${data.dev_otp}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp.trim() || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      // Use XMLHttpRequest to avoid fetch body stream issues
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/patient-portal-v2/verify-otp`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = () => {
          try {
            const response = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(response);
            } else {
              reject(new Error(response.detail || 'Verification failed'));
            }
          } catch (e) {
            reject(new Error('Server returned an invalid response'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error. Please try again.'));
        xhr.send(JSON.stringify({ 
          identifier: identifier.trim(), 
          identifier_type: identifierType,
          otp: otp.trim()
        }));
      });
      
      console.log('Verify OTP response:', data);
      console.log('Hospitals received:', data.hospitals?.length, data.hospitals?.map(h => h.hospital_name));
      
      // Login successful - pass data to parent
      onLogin(data);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo & Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 mb-4 shadow-lg">
            <Heart className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Zesta Healthcare</h1>
          <p className="text-slate-600 mt-2">Patient Portal</p>
        </div>

        <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl text-slate-900">
              {step === 'identifier' ? 'Welcome Back' : 'Verify OTP'}
            </CardTitle>
            <CardDescription className="text-slate-600">
              {step === 'identifier' 
                ? 'Sign in to access your health records'
                : `Enter the code sent to your ${identifierType}`
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {step === 'identifier' ? (
              <div className="space-y-6">
                {/* Identifier Type Selector */}
                <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                  <button
                    type="button"
                    onClick={() => { setIdentifierType('phone'); setIdentifier(''); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      identifierType === 'phone' 
                        ? 'bg-white shadow text-purple-700' 
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Phone className="h-4 w-4" />
                    Phone
                  </button>
                  <button
                    type="button"
                    onClick={() => { setIdentifierType('email'); setIdentifier(''); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      identifierType === 'email' 
                        ? 'bg-white shadow text-purple-700' 
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Mail className="h-4 w-4" />
                    Email
                  </button>
                </div>

                {/* Identifier Input */}
                <div>
                  <Label className="text-slate-700 font-medium">
                    {identifierType === 'phone' ? 'Phone Number' : 'Email Address'}
                  </Label>
                  <div className="relative mt-2">
                    {identifierType === 'phone' ? (
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    ) : (
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    )}
                    <Input
                      type={identifierType === 'phone' ? 'tel' : 'email'}
                      value={identifier}
                      onChange={(e) => setIdentifier(formatIdentifier(e.target.value))}
                      placeholder={identifierType === 'phone' ? '9876543210' : 'patient@email.com'}
                      className="pl-12 h-12 text-lg"
                      onKeyDown={(e) => e.key === 'Enter' && requestOTP()}
                    />
                  </div>
                  {identifierType === 'phone' && (
                    <p className="text-xs text-slate-500 mt-2">+91 will be added automatically</p>
                  )}
                </div>

                <Button 
                  onClick={requestOTP} 
                  className="w-full h-12 text-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  disabled={loading || !identifier.trim()}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="h-5 w-5 mr-2" />
                  )}
                  {loading ? 'Sending...' : 'Get OTP'}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* OTP Input */}
                <div>
                  <Label className="text-slate-700 font-medium">Enter 6-digit OTP</Label>
                  <div className="relative mt-2">
                    <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="pl-12 h-12 text-xl text-center tracking-[0.5em] font-mono"
                      maxLength={6}
                      autoFocus
                      onKeyDown={(e) => e.key === 'Enter' && verifyOTP()}
                    />
                  </div>
                </div>

                <Button 
                  onClick={verifyOTP} 
                  className="w-full h-12 text-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  disabled={loading || otp.length < 6}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="h-5 w-5 mr-2" />
                  )}
                  {loading ? 'Verifying...' : 'Verify & Sign In'}
                </Button>

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => { setStep('identifier'); setOtp(''); }}
                    className="text-sm text-slate-600 hover:text-slate-900"
                  >
                    Change {identifierType}
                  </button>
                  <button
                    type="button"
                    onClick={requestOTP}
                    disabled={countdown > 0}
                    className={`text-sm ${countdown > 0 ? 'text-slate-400' : 'text-purple-600 hover:text-purple-700'}`}
                  >
                    {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Access your health records from any hospital on Zesta Healthcare
        </p>
      </motion.div>
    </div>
  );
}

// ==================== HOSPITAL SELECTOR ====================
function HospitalSelector({ user, hospitals, onSelect, branding }) {
  const [loading, setLoading] = useState(null);

  const selectHospital = async (hospital) => {
    setLoading(hospital.hospital_id);
    try {
      const token = localStorage.getItem('portal_user_token');
      
      // Use XMLHttpRequest to avoid fetch body stream issues
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/patient-portal-v2/select-hospital`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => {
          try {
            const response = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(response);
            } else {
              reject(new Error(response.detail || 'Failed to select hospital'));
            }
          } catch (e) {
            reject(new Error('Server returned an invalid response'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error. Please try again.'));
        xhr.send(JSON.stringify({ hospital_id: hospital.hospital_id }));
      });
      
      onSelect(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 mb-4 shadow-lg">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Welcome, {user.full_name}!</h1>
          <p className="text-slate-600 mt-2">Which records would you like to view?</p>
        </div>

        {/* Hospital Cards */}
        <div className="space-y-4">
          {hospitals.map((hospital) => {
            const brandingColors = hospital.branding || {};
            const primaryColor = brandingColors.primary_color || '#6B21A8';
            
            return (
              <motion.div
                key={hospital.hospital_id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card 
                  className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 bg-white"
                  style={{ borderLeftColor: primaryColor }}
                  onClick={() => selectHospital(hospital)}
                >
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {hospital.branding?.logo_url ? (
                        <img 
                          src={hospital.branding.logo_url} 
                          alt={hospital.hospital_name}
                          className="w-12 h-12 object-contain rounded"
                        />
                      ) : (
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {hospital.hospital_name?.charAt(0) || 'H'}
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-slate-900">{hospital.hospital_name}</h3>
                        <p className="text-sm text-slate-500">
                          Patient ID: {hospital.patient_number}
                        </p>
                        {hospital.last_visit && (
                          <p className="text-xs text-slate-400">
                            Last visit: {new Date(hospital.last_visit).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    {loading === hospital.hospital_id ? (
                      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    ) : (
                      <ArrowRight className="h-6 w-6 text-slate-400" />
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Logout */}
        <div className="mt-8 text-center">
          <button
            onClick={() => {
              localStorage.removeItem('portal_user_token');
              localStorage.removeItem('portal_session_token');
              localStorage.removeItem('portal_user_data');
              window.location.reload();
            }}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            Sign out
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ==================== MAIN DASHBOARD ====================
function PatientDashboard({ session, onLogout, onSwitchHospital }) {
  const { active_hospital, patient } = session;
  const branding = active_hospital.branding || {};
  const primaryColor = branding.primary_color || '#6B21A8';
  
  const [activeTab, setActiveTab] = useState('overview');
  const [appointments, setAppointments] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const token = localStorage.getItem('portal_session_token');

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Use apiRequest helper to avoid fetch body stream issues
      const [aptsData, rxData, visitsData] = await Promise.all([
        apiRequest(`${API_URL}/api/patient-portal-v2/appointments`, { method: 'GET', headers }).catch(() => ({})),
        apiRequest(`${API_URL}/api/patient-portal-v2/prescriptions`, { method: 'GET', headers }).catch(() => ({})),
        apiRequest(`${API_URL}/api/patient-portal-v2/visits`, { method: 'GET', headers }).catch(() => ({}))
      ]);
      
      setAppointments(aptsData.appointments || []);
      setPrescriptions(rxData.prescriptions || []);
      setVisits(visitsData.visits || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const navItems = [
    { id: 'overview', label: 'Overview', icon: Home },
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'prescriptions', label: 'Prescriptions', icon: Pill },
    { id: 'visits', label: 'Visits', icon: Activity },
    { id: 'records', label: 'Records', icon: FileText },
  ];

  // Apply dynamic branding to CSS variables
  useEffect(() => {
    document.documentElement.style.setProperty('--hospital-primary', primaryColor);
  }, [primaryColor]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header 
        className="sticky top-0 z-50 shadow-md"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {branding.logo_url ? (
                <img 
                  src={branding.logo_url} 
                  alt={active_hospital.name}
                  className="h-10 w-auto bg-white rounded p-1"
                />
              ) : (
                <div className="w-10 h-10 rounded bg-white/20 flex items-center justify-center text-white font-bold">
                  {active_hospital.name?.charAt(0) || 'H'}
                </div>
              )}
              <div className="text-white">
                <h1 className="font-semibold">{active_hospital.name}</h1>
                <p className="text-xs text-white/80">Patient Portal</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={onSwitchHospital}
                className="text-white hover:bg-white/20"
              >
                <Building2 className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Switch Clinic</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onLogout}
                className="text-white hover:bg-white/20"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Patient Info Card */}
        <Card className="mb-6 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div 
                className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold"
                style={{ backgroundColor: primaryColor }}
              >
                {patient.full_name?.charAt(0) || 'P'}
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-slate-900">{patient.full_name}</h2>
                <p className="text-slate-600">Patient ID: {patient.patient_number}</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Navigation Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white shadow-sm p-1 rounded-lg border border-slate-200">
            {navItems.map(item => (
              <TabsTrigger 
                key={item.id} 
                value={item.id}
                className="text-slate-700 data-[state=active]:shadow-sm data-[state=active]:text-slate-900"
                style={{ 
                  '--tw-shadow-color': primaryColor,
                }}
              >
                <item.icon className="h-4 w-4 mr-2" />
                {item.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin" style={{ color: primaryColor }} />
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-6">
                {/* Stats Cards */}
                <Card className="bg-white shadow-sm">
                  <CardContent className="p-6 text-center">
                    <Calendar className="h-8 w-8 mx-auto mb-2" style={{ color: primaryColor }} />
                    <p className="text-3xl font-bold text-slate-900">{appointments.length}</p>
                    <p className="text-slate-600">Appointments</p>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-sm">
                  <CardContent className="p-6 text-center">
                    <Pill className="h-8 w-8 mx-auto mb-2" style={{ color: primaryColor }} />
                    <p className="text-3xl font-bold text-slate-900">{prescriptions.length}</p>
                    <p className="text-slate-600">Prescriptions</p>
                  </CardContent>
                </Card>
                <Card className="bg-white shadow-sm">
                  <CardContent className="p-6 text-center">
                    <Activity className="h-8 w-8 mx-auto mb-2" style={{ color: primaryColor }} />
                    <p className="text-3xl font-bold text-slate-900">{visits.length}</p>
                    <p className="text-slate-600">Visits</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-slate-900">Your Appointments</CardTitle>
              </CardHeader>
              <CardContent>
                {appointments.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No appointments found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {appointments.map((apt, idx) => (
                      <div key={apt.id || idx} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-slate-900">
                              {new Date(apt.date).toLocaleDateString()}
                            </p>
                            <p className="text-slate-600">Time: {apt.time}</p>
                            <p className="text-slate-600">Doctor: {apt.doctor_name || 'N/A'}</p>
                          </div>
                          <Badge style={{ backgroundColor: primaryColor, color: 'white' }}>
                            {apt.status || 'Scheduled'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Prescriptions Tab */}
          <TabsContent value="prescriptions">
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-slate-900">Your Prescriptions</CardTitle>
              </CardHeader>
              <CardContent>
                {prescriptions.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Pill className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No prescriptions found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {prescriptions.map((rx, idx) => (
                      <div key={rx.id || idx} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium text-slate-900">
                              {new Date(rx.created_at).toLocaleDateString()}
                            </p>
                            <p className="text-slate-600">Doctor: {rx.doctor_name || 'N/A'}</p>
                          </div>
                          <Badge variant="outline">
                            {rx.medications?.length || 0} medications
                          </Badge>
                        </div>
                        {rx.medications && rx.medications.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-100">
                            <p className="text-sm text-slate-500 mb-2">Medications:</p>
                            <div className="space-y-1">
                              {rx.medications.map((med, medIdx) => (
                                <p key={medIdx} className="text-sm text-slate-700">
                                  • {med.name} - {med.dosage} ({med.frequency})
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Visits Tab */}
          <TabsContent value="visits">
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-slate-900">Visit History</CardTitle>
              </CardHeader>
              <CardContent>
                {visits.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No visits found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {visits.map((visit, idx) => (
                      <div key={visit.id || idx} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-slate-900">
                              {new Date(visit.visit_date).toLocaleDateString()}
                            </p>
                            <p className="text-slate-600">Type: {visit.visit_type || 'General'}</p>
                            <p className="text-slate-600">Doctor: {visit.doctor_name || 'N/A'}</p>
                            {visit.chief_complaint && (
                              <p className="text-slate-500 text-sm mt-1">
                                Reason: {visit.chief_complaint}
                              </p>
                            )}
                          </div>
                          <Badge 
                            className={
                              visit.status === 'completed' ? 'bg-green-100 text-green-700' :
                              visit.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }
                          >
                            {visit.status || 'Completed'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Records Tab */}
          <TabsContent value="records">
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-slate-900">Medical Records</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Your medical records will appear here</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ==================== MAIN PORTAL COMPONENT ====================
export default function MultiTenantPatientPortal() {
  const [state, setState] = useState({
    phase: 'loading', // loading, login, select_hospital, dashboard
    user: null,
    hospitals: [],
    session: null,
    userToken: null
  });

  useEffect(() => {
    // Check for existing session
    const sessionToken = localStorage.getItem('portal_session_token');
    const sessionData = localStorage.getItem('portal_session_data');
    
    if (sessionToken && sessionData) {
      try {
        const session = JSON.parse(sessionData);
        setState({
          phase: 'dashboard',
          user: null,
          hospitals: [],
          session: session,
          userToken: null
        });
        return;
      } catch (e) {
        console.error('Invalid session data');
      }
    }
    
    // Check for user token (pre-hospital selection)
    const userToken = localStorage.getItem('portal_user_token');
    const userData = localStorage.getItem('portal_user_data');
    const hospitalsData = localStorage.getItem('portal_hospitals_data');
    
    if (userToken && userData && hospitalsData) {
      try {
        const user = JSON.parse(userData);
        const hospitals = JSON.parse(hospitalsData);
        setState({
          phase: 'select_hospital',
          user: user,
          hospitals: hospitals,
          session: null,
          userToken: userToken
        });
        return;
      } catch (e) {
        console.error('Invalid user data');
      }
    }
    
    // No valid session, show login
    setState(prev => ({ ...prev, phase: 'login' }));
  }, []);

  const handleLogin = (data) => {
    console.log('handleLogin called with hospitals:', data.hospitals?.length, data.hospitals);
    
    // Store user token and data
    localStorage.setItem('portal_user_token', data.access_token);
    localStorage.setItem('portal_user_data', JSON.stringify(data.user));
    localStorage.setItem('portal_hospitals_data', JSON.stringify(data.hospitals));
    
    // If only one hospital, auto-select it
    if (data.hospitals.length === 1) {
      console.log('Auto-selecting single hospital');
      // Auto-select the only hospital
      handleHospitalSelect({
        access_token: data.access_token, // Will be replaced by session token
        active_hospital: data.hospitals[0],
        patient: {} // Will be populated by select-hospital endpoint
      }, data.hospitals[0]);
    } else {
      console.log('Showing hospital selector for', data.hospitals.length, 'hospitals');
      setState({
        phase: 'select_hospital',
        user: data.user,
        hospitals: data.hospitals,
        session: null,
        userToken: data.access_token
      });
    }
  };

  const handleHospitalSelect = async (sessionData, hospital = null) => {
    // If coming from login with auto-select, we need to call the API
    if (hospital && !sessionData.patient?.id) {
      try {
        sessionData = await apiRequest(`${API_URL}/api/patient-portal-v2/select-hospital`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('portal_user_token')}`
          },
          body: JSON.stringify({ hospital_id: hospital.hospital_id })
        });
      } catch (error) {
        toast.error(error.message);
        return;
      }
    }
    
    // Store session
    localStorage.setItem('portal_session_token', sessionData.access_token);
    localStorage.setItem('portal_session_data', JSON.stringify(sessionData));
    
    setState({
      phase: 'dashboard',
      user: state.user,
      hospitals: state.hospitals,
      session: sessionData,
      userToken: null
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_user_token');
    localStorage.removeItem('portal_session_token');
    localStorage.removeItem('portal_user_data');
    localStorage.removeItem('portal_hospitals_data');
    localStorage.removeItem('portal_session_data');
    
    setState({
      phase: 'login',
      user: null,
      hospitals: [],
      session: null,
      userToken: null
    });
  };

  const handleSwitchHospital = async () => {
    // Clear session but keep user data
    localStorage.removeItem('portal_session_token');
    localStorage.removeItem('portal_session_data');
    
    // Fetch fresh hospital list from API
    const userToken = localStorage.getItem('portal_user_token');
    
    if (userToken) {
      try {
        const data = await apiRequest(`${API_URL}/api/patient-portal-v2/hospitals`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${userToken}`
          }
        });
        
        const hospitals = data.hospitals || [];
        
        // Update localStorage with fresh data
        localStorage.setItem('portal_hospitals_data', JSON.stringify(hospitals));
        
        const userData = localStorage.getItem('portal_user_data');
        if (userData) {
          setState({
            phase: 'select_hospital',
            user: JSON.parse(userData),
            hospitals: hospitals,
            session: null,
            userToken: userToken
          });
          return;
        }
      } catch (error) {
        console.error('Failed to fetch hospitals:', error);
      }
    }
    
    // Fallback to logout if fetch fails
    handleLogout();
  };

  // Render based on current phase
  if (state.phase === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (state.phase === 'login') {
    return <MultiTenantLogin onLogin={handleLogin} />;
  }

  if (state.phase === 'select_hospital') {
    return (
      <HospitalSelector 
        user={state.user}
        hospitals={state.hospitals}
        onSelect={handleHospitalSelect}
      />
    );
  }

  if (state.phase === 'dashboard' && state.session) {
    return (
      <PatientDashboard 
        session={state.session}
        onLogout={handleLogout}
        onSwitchHospital={handleSwitchHospital}
      />
    );
  }

  return null;
}
